import React from 'react';
import { useAccount } from '../../context/AccountContext';
import { useAccounts } from '../../hooks/useAccounts';
import '../../styles/PropertySwitcher.css';

/**
 * AccountSwitcher Component - v2.0 Multi-Page Support
 *
 * Global account switcher for admin/ops users
 * - Uses Context for state management (persists across pages)
 * - Uses React Query for caching and auto-retry
 * - Selection persists on page refresh (SessionStorage)
 *
 * Pattern: Follows Salesforce/Stripe account switching model
 * Location: /components/common/ - Reusable across all pages
 *
 * Usage: Simply add <AccountSwitcher /> to any page - no props needed
 */
const AccountSwitcher = () => {
  // Global state from Context
  const { selectedAccount, setSelectedAccount } = useAccount();

  // Data fetching with React Query (cached, auto-retry)
  const { data: accounts, isLoading, error } = useAccounts();

  const handleAccountChange = (accountId) => {
    setSelectedAccount(accountId === 'all' ? null : accountId);
  };

  if (isLoading) {
    return (
      <div className="property-switcher" dir="rtl">
        <label>
          <i className="bi bi-person-circle"></i>
          الحساب
        </label>
        <select className="property-select" disabled>
          <option>جاري التحميل...</option>
        </select>
      </div>
    );
  }

  if (error) {
    return (
      <div className="property-switcher" dir="rtl">
        <label>
          <i className="bi bi-person-circle"></i>
          الحساب
        </label>
        <select className="property-select" disabled>
          <option>فشل في جلب الحسابات</option>
        </select>
      </div>
    );
  }

  if (!accounts || accounts.length === 0) {
    return null;
  }

  // Helper function to format account display name with status
  const formatAccountName = (account) => {
    const statusLabel = account.status === 'pending' ? ' - معلق' : '';
    return `${account.name} (${account.email})${statusLabel}`;
  };

  return (
    <div className="property-switcher" dir="rtl">
      <label>
        <i className="bi bi-person-circle"></i>
        الحساب
      </label>
      <select
        value={selectedAccount || 'all'}
        onChange={(e) => handleAccountChange(e.target.value)}
        className="property-select"
      >
        <option value="all">
          جميع الحسابات ({accounts.length})
        </option>
        {accounts.map(account => (
          <option key={account._id} value={account._id}>
            {formatAccountName(account)}
          </option>
        ))}
      </select>
    </div>
  );
};

export default AccountSwitcher;
